export const layoutTree = (treeData, collapsedNodes, showEmptySlots = false, isMobile = false, renderedNodeSize = null, viewRootNodeId = null) => {
  const nodePositions = new Map();
  const emptySlots = new Map();
  const layoutItems = []; // Unified structure: all slots (occupied and empty)
  // Mobile: 80px level height, 40px nodes, 12px gap; Desktop: 120px height, 50px nodes, 15px gap.
  // These are fallback defaults for callers that don't pass renderedNodeSize
  // (e.g. treeLayout.test.js) -- Genealogy.jsx passes the ACTUAL rendered
  // circle diameter (getNodeSize(zoom).width), since that varies by zoom
  // level (20/40/80/100px) while this constant never did. Without this,
  // spacing math assumed a fixed 50px node at every zoom level, so nodes
  // rendered at 80-100px (zoom >= 1) visually overlapped despite their
  // computed positions being non-overlapping at the assumed 50px size.
  const levelHeight = isMobile ? 80 : 120;
  const nodeSize = renderedNodeSize != null ? renderedNodeSize : (isMobile ? 40 : 50);
  const minSiblingGap = isMobile ? 12 : 15;

  // Minimum width reserved for an empty leg. Kept as its own constant
  // (not reused from nodeSize) so empty legs can be tuned to avoid
  // visually collapsing into an adjacent large subtree, independently
  // of the node's own visual diameter.
  const minimumLegWidth = nodeSize;

  // The node to lay out FROM -- normally the tree's true root, but a
  // caller can pass a different node here to "re-root" the visible
  // layout at that node (Genealogy.jsx uses this so clicking a node
  // makes it the new top-of-screen, showing its own children below,
  // instead of only panning/centering within the full tree).
  const layoutRootId = viewRootNodeId != null ? viewRootNodeId : treeData.rootNodeId;

  if (!layoutRootId) return { nodePositions, emptySlots, layoutItems };

  // ── SINGLE SOURCE OF TRUTH FOR HORIZONTAL SIZE ──────────────────────────
  // Each node's 3 child legs gets ONLY the width its own subtree actually
  // needs (not a uniform forced max). totalWidth = sum of 3 real leg widths
  // + 2 gaps (not 3 * slot). A parent's reserved width for a child leg MUST
  // exactly equal getSubtreeMetrics(childId).totalWidth (cached and reused).
  // This produces proportional spacing: one deep leg doesn't inflate
  // space reserved for empty siblings.
  //
  // Collapsed nodes report legWidths: null (true leaves), with no leg-packing
  // recursion — no impossible geometry (nodeSize ≠ 3*nodeSize + 2*gap).
  //
  // Cache is scoped to this single call of layoutTree (created here, not at
  // module scope) since layoutTree re-runs fully on every render — a
  // module-level cache would leak stale widths across renders when
  // collapsedNodes changes or tree data updates.
  const subtreeMetricsCache = new Map();

  // Builds a leg->childId lookup once per node.
  const getChildrenByLeg = (nodeId) => {
    const map = new Map();
    treeData.getChildren(nodeId).forEach((childId) => {
      const leg = treeData.getNode(childId)?.leg_position;
      if (!leg) return;
      if (map.has(leg)) {
        console.error('Duplicate child in leg position', { nodeId, leg, existing: map.get(leg), duplicate: childId });
      }
      map.set(leg, childId);
    });
    return map;
  };

  // Returns { totalWidth, legWidths: [w1, w2, w3] | null, hasOccupiedDescendants: boolean }
  // legWidths is null for collapsed nodes (they're leaves — layoutNode must
  // check this and skip leg-packing/recursion for collapsed nodes).
  // hasOccupiedDescendants is true if this node has any occupied descendants.
  const getSubtreeMetrics = (nodeId) => {
    if (subtreeMetricsCache.has(nodeId)) return subtreeMetricsCache.get(nodeId);

    // The layout root itself is never treated as collapsed, even if it's
    // present in collapsedNodes (e.g. it was manually collapsed before
    // being re-rooted to) -- re-rooting onto a node is always meant to
    // reveal that node's own children/empty legs, not render it as a
    // terminal leaf.
    if (nodeId !== layoutRootId && collapsedNodes.has(nodeId)) {
      // True leaf: no legWidths, no children reserved — layoutNode must
      // check this and skip leg-packing/recursion for collapsed nodes.
      const metrics = { totalWidth: nodeSize, legWidths: null, hasOccupiedDescendants: false };
      subtreeMetricsCache.set(nodeId, metrics);
      return metrics;
    }

    const childrenByLeg = getChildrenByLeg(nodeId);
    const legWidths = [1, 2, 3].map((leg) => {
      const childId = childrenByLeg.get(leg);
      if (!childId) return minimumLegWidth; // empty leg's own named constant
      return getSubtreeMetrics(childId).totalWidth;
    });

    const totalWidth = Math.max(
      legWidths[0] + minSiblingGap + legWidths[1] + minSiblingGap + legWidths[2],
      nodeSize
    );

    // Check if any occupied child has occupied descendants
    const hasOccupiedDescendants = Array.from(childrenByLeg.values()).some(childId => {
      const childMetrics = getSubtreeMetrics(childId);
      return childMetrics.hasOccupiedDescendants || childrenByLeg.size > 0;
    });

    const metrics = { totalWidth, legWidths, hasOccupiedDescendants };
    subtreeMetricsCache.set(nodeId, metrics);
    return metrics;
  };

  // Position node and recursively position children
  const layoutNode = (nodeId, level, xStart) => {
    const y = level * levelHeight; // unchanged — Genealogy.jsx:928 depends on this
    const { totalWidth, legWidths } = getSubtreeMetrics(nodeId);

    // Node's own center = middle of the span [xStart, xStart + totalWidth]
    const x = xStart + totalWidth / 2;
    nodePositions.set(nodeId, { x, y });

    // Collapsed node: render itself only, no leg-position computation,
    // no recursion, no empty slots — matches current collapsed behavior.
    if (legWidths === null) {
      return;
    }

    // Pack legs centered on parent: CENTER leg directly under parent,
    // LEFT and RIGHT positioned asymmetrically -- each side reserves
    // only ITS OWN width, not Math.max(w1, w3). The old symmetric
    // version pushed a narrow/empty side out to match however wide the
    // OTHER side's real subtree was, roughly doubling total width for
    // any node with one deep branch and one empty sibling -- the main
    // cause of the tree ballooning sideways for mostly-empty trees.
    const legPositions = {};
    const w1 = legWidths[0];
    const w2 = legWidths[1];
    const w3 = legWidths[2];

    // Center leg is at parent's x position
    legPositions[2] = { x: x, width: w2 };

    // Left leg: positioned using its OWN half-width, not the right
    // side's.
    legPositions[1] = {
      x: x - (w2 / 2 + minSiblingGap + w1 / 2),
      width: w1
    };

    // Right leg: positioned using its OWN half-width, not the left
    // side's.
    legPositions[3] = {
      x: x + (w2 / 2 + minSiblingGap + w3 / 2),
      width: w3
    };

    // Generate layout items for all three slots (occupied and empty)
    const children = treeData.getChildren(nodeId);
    const childrenByLegForLayout = new Map();
    children.forEach(childId => {
      const childNode = treeData.getNode(childId);
      if (childNode?.leg_position) {
        childrenByLegForLayout.set(childNode.leg_position, childId);
      }
    });

    // Create unified layout items for all three slots
    for (let leg = 1; leg <= 3; leg++) {
      const childId = childrenByLegForLayout.get(leg);
      const childX = legPositions[leg].x;
      const childY = (level + 1) * levelHeight;
      const occupied = !!childId;

      const layoutItem = {
        id: occupied ? `node-${childId}` : `empty-${nodeId}-leg-${leg}`,
        parentId: nodeId,
        slotIndex: leg - 1, // 0, 1, 2
        occupied,
        nodeId: childId || null,
        x: childX,
        y: childY,
        leg
      };

      layoutItems.push(layoutItem);

      // Also populate the old structures for backward compatibility
      if (occupied) {
        // Already handled by main layout pass
      } else if (showEmptySlots) {
        const emptySlotKey = `${nodeId}-leg-${leg}`;
        emptySlots.set(emptySlotKey, { x: childX, y: childY, leg, parentNodeId: nodeId });
      }
    }

    // Recursively position children at their correct leg positions
    const childrenByLeg = getChildrenByLeg(nodeId);
    childrenByLeg.forEach((childId, leg) => {
      const childMetrics = getSubtreeMetrics(childId); // child's OWN true metrics
      const childWidth = childMetrics.totalWidth;
      const EPSILON = 0.001;
      console.assert(
        Math.abs(childWidth - legPositions[leg].width) < EPSILON,
        'Child width does not match its own subtree-width allocation', childId, childWidth, legPositions[leg].width
      );
      const childXStart = legPositions[leg].x - childWidth / 2; // centered within its leg
      layoutNode(childId, level + 1, childXStart);
    });
  };

  // Start layout centered at x = 0
  const rootMetrics = getSubtreeMetrics(layoutRootId);
  layoutNode(layoutRootId, 0, -rootMetrics.totalWidth / 2);

  return { nodePositions, emptySlots, layoutItems };
};

export const calculateBounds = (nodePositions, nodeSize = 80, layoutItems = []) => {
  if (nodePositions.size === 0 && layoutItems.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0, centerX: 0, centerY: 0 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  nodePositions.forEach(({ x, y }) => {
    minX = Math.min(minX, x - nodeSize / 2);
    maxX = Math.max(maxX, x + nodeSize / 2);
    minY = Math.min(minY, y - nodeSize / 2);
    maxY = Math.max(maxY, y + nodeSize / 2);
  });

  // Include empty slot positions in bounds
  layoutItems.forEach(({ x, y }) => {
    minX = Math.min(minX, x - nodeSize / 2);
    maxX = Math.max(maxX, x + nodeSize / 2);
    minY = Math.min(minY, y - nodeSize / 2);
    maxY = Math.max(maxY, y + nodeSize / 2);
  });

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

export const fitViewport = (bounds, viewportWidth, viewportHeight, padding = 50) => {
  if (bounds.width === 0 || bounds.height === 0) {
    return { zoom: 1, pan: { x: 0, y: 0 } };
  }

  const scaleX = (viewportWidth - padding * 2) / bounds.width;
  const scaleY = (viewportHeight - padding * 2) / bounds.height;
  const zoom = Math.min(scaleX, scaleY, 1.5);

  const pan = {
    x: viewportWidth / 2 - bounds.centerX * zoom,
    y: viewportHeight / 2 - bounds.centerY * zoom,
  };

  return { zoom, pan };
};

export const centerRootNode = (rootPos, viewportWidth, viewportHeight, zoom) => {
  if (!rootPos) {
    return { x: 0, y: 0 };
  }

  return {
    x: viewportWidth / 2 - rootPos.x * zoom,
    y: viewportHeight / 2 - rootPos.y * zoom,
  };
};
